import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAccessDocument } from '../services/publicDocument.service';

export const DocumentAccessPage = (): JSX.Element => {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const hasOpenedRef = useRef(false);

  const { data, isLoading, isError, error } = useAccessDocument(token ?? '');

  useEffect(() => {
    if (data?.url && !hasOpenedRef.current) {
      hasOpenedRef.current = true;
      window.open(data.url, '_blank');
    }
  }, [data]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">{t('document_access.loading')}</p>
        </div>
      </div>
    );
  }

  if (isError) {
    const errorMessage = getErrorMessage(error, t);
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            {t('document_access.error.title')}
          </h1>
          <p className="text-gray-600">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (data) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{data.documentName}</h1>
          <p className="text-gray-600 mb-6">{t('document_access.success.description')}</p>
          <button
            onClick={() => window.open(data.url, '_blank')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-md hover:bg-opacity-90 transition"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
            {t('document_access.open_document')}
          </button>
        </div>
      </div>
    );
  }

  return <></>;
};

const getErrorMessage = (error: unknown, t: (key: string) => string): string => {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as { response?: { status?: number; data?: { message?: string } } };
    const status = axiosError.response?.status;
    const message = axiosError.response?.data?.message;

    if (status === 401) {
      if (message?.includes('expired')) {
        return t('document_access.error.expired');
      }
      return t('document_access.error.invalid');
    }
    if (status === 404) {
      return t('document_access.error.not_found');
    }
  }
  return t('document_access.error.generic');
};
